'use client'

import { FC, FormEvent, ReactElement, useId, useState } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'

// Same shape /api/subscribe validates against, so the bar rejects locally
// what the relay would reject anyway.
const EMAIL_REGEX = /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{2,}$/

type Status = 'idle' | 'submitting' | 'success' | 'invalid' | 'failed'

/// Email sign-up that lives inside the fixed h-8 footer bar. Every control is
/// h-6 so the bar's height never changes, and feedback swaps text in place
/// (no toast, no row underneath) for the same reason.
///
/// `stacked` is the roomy layout for the mobile drawer: the label always shows
/// on its own line and the input takes the full width.
const FooterSubscribe: FC<{ className?: string; stacked?: boolean }> = ({
	className,
	stacked
}): ReactElement => {
	const inputId = useId()
	const [email, setEmail] = useState('')
	const [status, setStatus] = useState<Status>('idle')

	const handleSubmit = async (evt: FormEvent<HTMLFormElement>) => {
		evt.preventDefault()
		if (status === 'submitting') return
		const value = email.trim()
		if (!EMAIL_REGEX.test(value)) {
			setStatus('invalid')
			return
		}
		setStatus('submitting')
		try {
			// No `list`: the relay defaults to the launch-alerts list.
			const res = await fetch('/api/subscribe', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ email: value })
			})
			if (!res.ok) throw new Error(String(res.status))
			setEmail('')
			setStatus('success')
		} catch {
			setStatus('failed')
		}
	}

	if (status === 'success') {
		return (
			<p
				role="status"
				data-testid="footer-subscribe-success"
				className={cn(
					'text-foreground flex items-center gap-1 whitespace-nowrap select-none',
					stacked ? 'text-sm' : 'text-xs',
					className
				)}
			>
				<Check className="size-3.5 text-primary" /> You&apos;re on the list
			</p>
		)
	}

	const error =
		status === 'invalid' ? 'Enter a valid email' : status === 'failed' ? 'Try again' : null

	return (
		<form
			noValidate
			onSubmit={handleSubmit}
			data-testid="footer-subscribe"
			className={cn(
				'flex items-center min-w-0',
				stacked ? 'flex-wrap gap-2' : 'justify-end gap-1.5',
				className
			)}
		>
			<label
				htmlFor={inputId}
				className={cn(
					'whitespace-nowrap select-none',
					stacked ? 'w-full text-sm font-medium' : 'text-xs hidden min-[1160px]:block',
					error ? 'text-destructive' : stacked ? 'text-foreground' : 'text-muted-foreground'
				)}
			>
				{error ?? 'Be the first to know'}
			</label>
			<input
				id={inputId}
				type="email"
				name="email"
				inputMode="email"
				autoComplete="email"
				placeholder="Email"
				aria-label="Email address"
				aria-invalid={status === 'invalid'}
				value={email}
				onChange={(evt) => {
					setEmail(evt.target.value)
					if (error) setStatus('idle')
				}}
				className={cn(
					'w-full min-w-0 flex-1 border bg-background text-foreground placeholder:text-muted-foreground focus-visible:outline-none',
					stacked
						? 'h-9 rounded-md px-3 text-sm'
						: 'h-6 rounded px-1.5 text-xs max-w-40 sm:flex-none sm:w-28',
					error ? 'border-destructive' : 'border-input focus-visible:border-primary'
				)}
			/>
			<button
				type="submit"
				disabled={status === 'submitting'}
				className={cn(
					'shrink-0 border border-border bg-muted leading-none font-medium text-muted-foreground hover:text-foreground disabled:opacity-60',
					stacked ? 'h-9 rounded-md px-3 text-xs' : 'h-6 rounded px-2 text-[11px]'
				)}
			>
				{status === 'submitting' ? '…' : status === 'failed' ? 'Retry' : 'Subscribe'}
			</button>
			<span role="status" className="sr-only">
				{error}
			</span>
		</form>
	)
}

export default FooterSubscribe
